/* eslint-disable @typescript-eslint/member-ordering */
import {computed, makeObservable, observable} from "mobx";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {Project} from "./project";
import {AbstractModel} from "./abstract-model";

export interface ContractorProps {
    objectId?: number;
    guid?: string;
    id?: number;
    organisation?: string;
    contact?: string;
    email?: string;
    street?: string;
    streetNumber?: string;
    postbox?: string;
    postalCode?: number;
    city?: string;
    addressLine?: string;   // TODO GK: needed for contractor-form?
    notes?: string;
    statusId?: number;
    creator?: string;
    createdAt?: Date;
    editor?: string;
    updatedAt?: Date;

}

/**
 * Auftragnehmer
 */
@JsonObject()
export class Contractor extends AbstractModel {

    static readonly comparableAttributes: string[] = [
        "id", "guid", "organisation", "contact", "email", "street", "streetNumber", "postbox", "postalCode", "city",
        "addressLine", "notes", "statusId"
    ];
    // workaround, because feature template doesn't seem to work for table features
    static readonly prototypeAttributes: string[] = [
        "OBJECTID", "ID", "GUID", "ORGANISATION", "KONTAKT", "EMAIL", "STRASSE", "HAUSNUMMER", "POSTFACH", "PLZ", "ORT",
        "ADRESSZUSATZ", "BEMERKUNGEN", "STATUS",
        "CREATED_USER", "CREATED_AT", "UPDATED_USER", "UPDATED_AT"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "OBJECTID"],
        ["guid", "GUID"],
        ["id", "ID"],
        ["organisation", "ORGANISATION"],
        ["contact", "KONTAKT"],
        ["email", "EMAIL"],
        ["street", "STRASSE"],
        ["streetNumber", "HAUSNUMMER"],
        ["postbox", "POSTFACH"],
        ["postalCode", "PLZ"],
        ["city", "ORT"],
        ["addressLine", "ADRESSZUSATZ"],
        ["notes", "BEMERKUNGEN"],
        ["statusId", "STATUS"],
        ["creator", "CREATED_USER"],
        ["createdAt", "CREATED_AT"],
        ["editor", "UPDATED_USER"],
        ["updatedAt", "UPDATED_AT"]
    ]);

    class = "Contractor";
    typeLabel = "Auftragnehmer";

    projects: Project[];
    projectsLoaded: boolean;

    static sortByOrganisation(a: Contractor, b: Contractor) {
        const nameA = a.organisation?.toUpperCase(); // ignore upper and lowercase
        const nameB = b.organisation?.toUpperCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    }

    static sortByName(a: Contractor, b: Contractor) {
        const nameA = a.displayName?.toUpperCase(); // ignore upper and lowercase
        const nameB = b.displayName?.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    }

    /**
     * Eindeutiger Identifikator
     */
    @JsonProperty({name: "GUID"})
        guid: string;

    /**
     * ID
     */
    @JsonProperty({name: "ID"})
        id: number;

    /**
     * Organisation
     */
    @JsonProperty({name: "ORGANISATION"})
        organisation: string;

    /**
     * Kontakt
     */
    @JsonProperty({name: "KONTAKT"})
        contact: string;

    /**
     * Email
     */
    @JsonProperty({name: "EMAIL"})
        email: string;

    /**
     * Strasse
     */
    @JsonProperty({name: "STRASSE"})
        street: string;

    /**
     * Hausnummer
     */
    @JsonProperty({name: "HAUSNUMMER"})
        streetNumber: string;

    /**
     * Postfach
     */
    @JsonProperty({name: "POSTFACH"})
        postbox: string;

    /**
     * Postleitzahl
     */
    @JsonProperty({name: "PLZ"})
        postalCode: number;

    /**
     * Ort
     */
    @JsonProperty({name: "ORT"})
        city: string;

    /**
     * Adresszusatz
     */
    @JsonProperty({name: "ADRESSZUSATZ"})
        addressLine: string;

    /**
     * Notizen, Bemerkungen
     */
    @JsonProperty({name: "BEMERKUNGEN"})
        notes: string;

    /**
     * Status
     */
    @JsonProperty({name: "STATUS"})
        statusId: number;

    constructor(props?: ContractorProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        // make observable after setting properties to avoid 'strict' messages
        makeObservable(this, {
            objectId: observable, // do observe to reload when object is saved
            id: observable,
            guid: observable,
            organisation: observable,
            contact: observable,
            email: observable,
            street: observable,
            streetNumber: observable,
            postalCode: observable,
            city: observable,
            postbox: observable,
            addressLine: observable,
            notes: observable,
            statusId: observable,
            projects: observable,
            projectsLoaded: observable,

            displayName: computed,
            fullAddress: computed,

            hasProjects: computed,
            isAlterable: computed,
            isDeletable: computed,

            creator: observable,
            createdAt: observable,
            editor: observable,
            updatedAt: observable,
            isNewRecord: computed
        });
    }

    get comparableAttributes(): string[] {
        return Contractor.comparableAttributes;
    }

    get displayName(): string {
        return `${this.organisation}, ${this.postalCode} ${this.city}`;
    }

    get fullAddress(): string {
        return `${this.street} ${this.streetNumber}, ${this.postalCode} ${this.city}`;
    }

    get hasProjects(): boolean {
        return this.projectsLoaded && this.projects?.length >= 1;
    }

    /**
     * If contractor properties are allowed to change.
     * Depends on relations to projects.
     */
    get isAlterable(): boolean {
        // TODO: Wann ist Editierbarkeit eingeschränkt? Was darf wann noch editiert werden?
        return true;
    }

    /**
     * If contractor can be deleted.
     * Depends on relations to projects.
     */
    get isDeletable(): boolean {
        return !this.hasProjects;
    }
}
